/**
 * API Client Utility for Alpine Components
 * Provides methods for making authenticated API calls to the InterPals REST API
 */

import { getJwtToken, clearJwtToken, initializeJwtToken } from './jwtTokenManager.js';

class ApiClient {
    constructor() {
        this.baseUrl = '/v1';
    }

    /**
     * Get API token from localStorage Redux state
     * @returns {string|null} API token or null if not found
     */
    getApiToken() {
        const tokenData = getJwtToken();
        return tokenData?.auth?.token || null;
    }

    /**
     * Get CSRF token from meta tag
     * @returns {string} CSRF token or empty string
     */
    getCsrfToken() {
        // Support both hyphenated and underscored meta names (templates use underscore)
        const metaToken = document.querySelector('meta[name="csrf-token"]')?.content
            || document.querySelector('meta[name="csrf_token"]')?.content;

        if (metaToken) {
            return metaToken;
        }

        // Fallback to hidden input if meta tag is missing
        const inputToken = document.querySelector('input[name="csrf_token"]')?.value;
        return inputToken || '';
    }

    /**
     * Make an API request
     * @param {string} endpoint - API endpoint path
     * @param {Object} options - Fetch options
     * @param {boolean} [isRetry] - Internal: true when this call is the
     *        single retry after a fresh JWT mint. Prevents loops.
     * @returns {Promise<Response>}
     */
    async request(endpoint, options = {}, isRetry = false) {
        const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;

        const headers = {
            'X-CSRF-TOKEN': this.getCsrfToken(),
            ...options.headers
        };

        // Add auth token if available (not needed for public endpoints like search/geo)
        const token = this.getApiToken();
        if (token) {
            headers['X-AUTH-TOKEN'] = token;
        }

        const response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include'
        });

        // A 401 on a request that carried an auth token usually means the
        // stored JWT has been invalidated server-side (session deleted, jti
        // pruned from `jwt_tokens`, user logged out elsewhere, ...). Mint a
        // fresh JWT bound to the still-valid PHP session and retry once.
        // No retry if (a) there was no token to begin with — that's a real
        // auth failure; (b) we already retried; (c) the refresh failed —
        // session genuinely invalid, surface the 401 to the caller. The
        // retry assumes the first request was rejected before any
        // server-side side-effect ran (true for `/v1/*` where auth runs
        // before the controller body). Mirrors `alpine-editor-base.js:103-117`.
        if (response.status === 401 && token && !isRetry) {
            // Without this, initializeJwtToken short-circuits on the still-
            // cached (invalidated) token and the retry carries the same bad
            // credentials, silently re-hitting 401 instead of refreshing.
            clearJwtToken();
            const refreshed = await initializeJwtToken();
            if (refreshed?.auth?.token) {
                return this.request(endpoint, options, true);
            }
        }

        return response;
    }

    /**
     * Search for cities
     * @param {Object} params - Search parameters
     * @returns {Promise<Object>} Search results
     */
    async searchGeo(params) {
        const queryParams = new URLSearchParams(params);
        const response = await this.request(`/search/geo?${queryParams}`, {
            method: 'GET'
        });

        if (!response.ok) {
            throw new Error(`Geo search failed: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Create a new account
     * @param {Object} data - Account data
     * @returns {Promise<Object>} Created account data with token
     */
    async createAccount(data) {
        const formData = new FormData();

        // Add all non-empty fields to FormData
        Object.keys(data).forEach(key => {
            if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
                formData.append(key, data[key]);
            }
        });

        const response = await this.request('/account', {
            method: 'POST',
            body: formData
        });

        // A proxy error page is not JSON. The status still names the failure.
        let result = null;
        try {
            result = await response.json();
        } catch (parseError) {
            if (response.ok) {
                throw parseError;
            }
        }

        if (!response.ok) {
            // Handle validation errors
            if (result && (response.status === 400 || response.status === 422)) {
                const error = new Error('Validation failed');
                // API returns errors nested under error.errors
                error.validationErrors = result.error?.errors || result.errors || {};
                throw error;
            }
            const error = new Error(result?.message || `Account creation failed: ${response.status}`);
            error.status = response.status;
            throw error;
        }

        return result;
    }

    /**
     * Record a device-geolocation observation captured during signup.
     *
     * Fire-and-forget: sent with `keepalive` so it survives the redirect that
     * follows account creation. Coordinates are only sent when present (a
     * granted fix); a refusal sends just the permission_status. Callers should
     * not await this and should swallow rejections.
     *
     * @param {{permission_status: string, lat: ?number, lon: ?number, accuracy: ?number}} observation
     * @param {{signup_context?: boolean}} [extra]
     * @returns {Promise<Response>|null} null when there is nothing to record
     */
    async postGeoObservation(observation, extra = {}) {
        if (!observation || !observation.permission_status) {
            return null;
        }

        // URLSearchParams (not FormData) because this fires during the
        // post-signup navigation: Safari drops `keepalive` requests with a
        // multipart FormData body while the page is unloading, but honors a
        // urlencoded one. The backend reads both content types identically.
        const body = new URLSearchParams();
        body.append('source', 'web');
        body.append('permission_status', observation.permission_status);
        if (observation.lat !== null && observation.lat !== undefined) {
            body.append('lat', observation.lat);
        }
        if (observation.lon !== null && observation.lon !== undefined) {
            body.append('lon', observation.lon);
        }
        if (observation.accuracy !== null && observation.accuracy !== undefined) {
            body.append('accuracy', observation.accuracy);
        }
        body.append('signup_context', extra.signup_context ? '1' : '0');

        return this.request('/geo-observation', {
            method: 'POST',
            body,
            keepalive: true
        });
    }

    /**
     * Check a signup username. A refusal carries the code and message that
     * POST /account gives the same username.
     * @param {string} username
     * @returns {Promise<{username: string, available: boolean, code: ?string, message: ?string}>}
     */
    async checkUsernameAvailability(username) {
        const query = new URLSearchParams({ username });
        const response = await this.request(`/username-availability?${query}`, { method: 'GET' });

        if (!response.ok) {
            throw new Error(`Username check failed: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Check a signup email address. A refusal carries the code and message
     * that POST /account gives the same address.
     * @param {string} email
     * @returns {Promise<{available: boolean, code: ?string, message: ?string}>}
     */
    async checkEmailAvailability(email) {
        const body = new URLSearchParams();
        body.append('email', email);

        const response = await this.request('/email-availability', {
            method: 'POST',
            body
        });

        if (!response.ok) {
            throw new Error(`Email check failed: ${response.status}`);
        }

        return response.json();
    }
}

// Create singleton instance
const apiClient = new ApiClient();

// Export for use in Alpine components
window.ApiClient = apiClient;

export default apiClient;
